import { i18n } from '../i18n.js';
/**
 * Spell Announce UI Module
 *
 * Floats full card faces in a centered overlay for a few seconds so a player
 * can actually READ what just happened:
 *  - a Spell cast (opponent, or your own Stellar cast), or
 *  - hidden cards were revealed to this player (socket `cardsRevealed`).
 *
 * Cards are rendered through the shared Cards.createCardElement so they are
 * pixel-identical to every other card surface. Announcements queue; hovering
 * the cards pauses the timer; clicking dismisses.
 */


import { CardDescEngraving } from './card-desc-engraving.js';
import { Cards } from './cards.js';
import { SoundEffects } from './sound-effects.js';
import { TouchPreview } from './touch-preview.js';
let SpellAnnounce = {};

SpellAnnounce = (function () {
    const timings = {
        holdMs: 4200,
        fadeMs: 350,
        extraPerCardMs: 1400,
        // Sticky floats (enemy casts) hold until the cast resolves…
        minStickyMs: 2200,   // …but never flash away faster than this
        stellarCastMinMs: 4200,
        stickySafetyMs: 90000, // …and never outlive a lost resolution signal
        /* HOW LONG A RESOLVED CAST IS HELD OPEN FOR ITS OWN ART.
           "never stop revealing the casted spell live as its loading unless
           player dissmissed it" — so a resolution that lands while the card is
           still painting does not close the float; it waits for the art and
           then grants the ordinary minimum read time from that moment. Bounded
           so a settlement promise that never settles cannot pin the overlay
           (the sticky safety net above is the outer bound either way). */
        mediaHoldMs: 6000
    };

    let overlayElement = null;
    let queue = [];
    let active = false;
    let sticky = false;
    let stickyResolved = false;
    let activeMinStickyMs = timings.minStickyMs;
    let displayedAt = 0;
    let hideTimer = null;
    let remainingMs = 0;
    let holdStartedAt = 0;
    let announceCounter = 0;
    let displayGeneration = 0;
    /* The float's card is still painting. A resolution that arrives now must
       not close it — see `mediaHoldMs`. */
    let activeMediaSettled = true;
    let mediaHoldTimer = null;
    let hideOnMediaSettled = false;

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        /* The FALLBACK is interpolated too. It is returned verbatim whenever
           i18n has not resolved — the normal case on a cold load — and the
           ui.json wording these fallbacks mirror carries `{{name}}`
           placeholders, so a user could be shown a literal `{{count}}`. Both
           brace styles are handled because the copy uses both. */
        const source = translated && translated !== key ? translated : fallback;
        return Object.entries(replacements || {}).reduce(
            (text, [name, value]) => String(text)
                .split(`{{${name}}}`).join(String(value))
                .split(`{${name}}`).join(String(value)),
            String(source == null ? key : source),
        );
    }

    function isElementVisible(element) {
        if (!element || element.hidden) return false;
        const style = window.getComputedStyle ? window.getComputedStyle(element) : element.style;
        return style.display !== 'none' && style.visibility !== 'hidden';
    }

    function isGameSurfaceActive() {
        return document.body?.classList?.contains('game-active')
            && isElementVisible(document.getElementById('game-view'))
            && !isElementVisible(document.getElementById('auth-view'))
            && !isElementVisible(document.getElementById('lobby-view'));
    }

    function labelFor(kind, playerName) {
        switch (kind) {
            case 'enemySpell':
                return playerName
                    ? uiText('spellAnnounce.enemyCastNamed', `${playerName} casts…`, { name: playerName })
                    : uiText('spellAnnounce.enemyCast', 'Enemy spell!');
            case 'yourStellarSpell':
                return uiText('spellAnnounce.yourCast', 'You cast…');
            case 'revealHand':
                return uiText('spellAnnounce.revealedHand', 'Revealed from the enemy hand');
            case 'revealDeckSelf':
                return uiText('spellAnnounce.revealedDeckSelf', 'A vision from your deck');
            case 'revealDeckEnemy':
                return uiText('spellAnnounce.revealedDeckEnemy', 'Glimpsed from the enemy deck');
            default:
                return uiText('spellAnnounce.revealed', 'Card revealed');
        }
    }

    function init() {
        if (document.getElementById('spell-announce-overlay')) {
            overlayElement = document.getElementById('spell-announce-overlay');
            return;
        }
        const overlay = document.createElement('div');
        overlay.id = 'spell-announce-overlay';
        overlay.setAttribute('aria-live', 'polite');
        document.body.appendChild(overlay);
        overlayElement = overlay;
    }

    function clearHideTimer() {
        if (hideTimer) {
            clearTimeout(hideTimer);
            hideTimer = null;
        }
    }

    function scheduleHide(ms) {
        clearHideTimer();
        holdStartedAt = Date.now();
        remainingMs = ms;
        hideTimer = setTimeout(hide, ms);
    }

    function pauseTimer() {
        if (!hideTimer) return;
        clearHideTimer();
        remainingMs = Math.max(500, remainingMs - (Date.now() - holdStartedAt));
    }

    function resumeTimer() {
        if (!active || hideTimer) return;
        scheduleHide(remainingMs);
    }

    function resolveQueuedSticky() {
        const queuedSticky = queue.find(announcement => (
            announcement && announcement.sticky === true && announcement.stickyResolved !== true
        ));
        if (!queuedSticky) return false;
        queuedSticky.stickyResolved = true;
        return true;
    }

    function clearMediaHold() {
        if (mediaHoldTimer) {
            clearTimeout(mediaHoldTimer);
            mediaHoldTimer = null;
        }
        activeMediaSettled = true;
        hideOnMediaSettled = false;
    }

    function hide() {
        displayGeneration += 1;
        clearHideTimer();
        clearMediaHold();
        sticky = false;
        stickyResolved = false;
        activeMinStickyMs = timings.minStickyMs;
        if (!overlayElement) return;
        overlayElement.classList.remove('visible');
        overlayElement.classList.add('leaving');
        window.dispatchEvent(new CustomEvent('moonfall:transient-change'));
        setTimeout(() => {
            if (!overlayElement) return;
            overlayElement.classList.remove('leaving');
            while (overlayElement.firstChild) overlayElement.removeChild(overlayElement.firstChild);
            active = false;
            showNext();
            window.dispatchEvent(new CustomEvent('moonfall:transient-change'));
        }, timings.fadeMs);
    }

    // Hover enlargement runs through the shared crisp engine (CSS zoom on
    // zoom engines, transform fallback elsewhere) exactly like hand cards —
    // a plain transform:scale() stretches the resting rasterization and
    // reads soft at 1.6×. The wrap is a fixed flex slot, so the zoomed
    // layout box centers itself without moving neighboring cards.
    const FLOAT_HOVER_OPTIONS = {
        baseScale: 1.6,
        minScale: 1.2,
        margin: 12,
        // Fit against the displayed box and take the transform path: the
        // float's raster is reserved at 1.6× (buildCardFace), so transform
        // scaling stays crisp, while CSS zoom would re-resolve the vmin card
        // sizing and eat most of the visible growth.
        fitDisplayed: true,
        allowZoom: false,
        origin: 'center center',
        translateYPercent: 0
    };

    /* The transform's transition time in cards.css is 0.2s; swap to the
       grown LAYOUT just after it settles. */
    const FLOAT_HOVER_SETTLE_MS = 240;

    function attachFloatHover(face) {
        // Touch: tap enlarges, tap again acts. The guard above already admits the
        // synthetic (untrusted) mouseenter this dispatches while rejecting the
        // compatibility one a real tap produces.
        TouchPreview?.attach?.(face);
        let settleTimer = null;
        face.addEventListener('mouseenter', (event) => {
            if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
            // Idempotence guard: the zoom branch of applyHoverTransform
            // multiplies any existing zoom, so a duplicate enter (e.g. a
            // synthetic event) without an interleaved leave would compound.
            if (face.dataset.floatHoverActive === 'true') return;
            face.dataset.floatHoverActive = 'true';
            /* The settle below re-lays-out the card at its grown size, where a
               line on its wrap point re-wraps. Engrave the resting lines now,
               while the face is still at rest, and keep them until the leave
               (card-desc-engraving.js). */
            CardDescEngraving.engrave(face);
            Cards?.applyHoverTransform?.(face, FLOAT_HOVER_OPTIONS);
            /* Once the growth animation settles, the transform hands off to a
               REAL layout at the grown size (the .spell-announce-grown unit
               multiplier): the same visual box, but the frame, text, and art
               now raster at the size actually displayed instead of stretching
               the rest-sized layer 1.6x — the remaining softness of the enemy
               reveal. The swap is same-size, transition-suppressed, and the
               raster refresh below re-samples the art canvas 1:1. */
            if (settleTimer) clearTimeout(settleTimer);
            settleTimer = setTimeout(() => {
                settleTimer = null;
                if (face.dataset.floatHoverActive !== 'true' || !face.isConnected) return;
                const applied = /scale\(([\d.]+)\)/.exec(
                    face.style.getPropertyValue('--card-hover-transform') || '',
                );
                const scale = applied ? Number(applied[1]) : FLOAT_HOVER_OPTIONS.baseScale;
                if (!(scale > 1)) return;
                face.style.setProperty('--spell-announce-hover-scale', String(scale));
                face.classList.add('spell-announce-grown');
                face.style.removeProperty('--card-hover-transform');
                try { Cards?.refreshCardRaster?.(face); } catch (_) { /* raster is decoration */ }
            }, FLOAT_HOVER_SETTLE_MS);
        });
        face.addEventListener('mouseleave', () => {
            delete face.dataset.floatHoverActive;
            if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
            const wasGrown = face.classList.contains('spell-announce-grown');
            face.classList.remove('spell-announce-grown');
            face.style.removeProperty('--spell-announce-hover-scale');
            face.style.zoom = '';
            face.style.removeProperty('--card-hover-transform');
            face.style.removeProperty('transform-origin');
            // At rest again: the natural paragraph, byte-identical.
            CardDescEngraving.release(face);
            if (wasGrown) {
                try { Cards?.refreshCardRaster?.(face); } catch (_) { /* raster is decoration */ }
            }
        });
    }

    function buildCardFace(card, index, compact) {
        announceCounter += 1;
        const wrap = document.createElement('div');
        wrap.className = 'spell-announce-card-wrap';
        wrap.style.animationDelay = `${index * 120}ms`;

        let face = null;
        if (Cards?.createCardElement) {
            try {
                face = Cards.createCardElement(
                    { ...card },
                    `spell-announce-${announceCounter}`,
                    /* Reserve stays 1.6: dropping it to 1 routes this surface
                       into ensureStaticTransferAuthority's strict reserve-1
                       path, which can ABANDON a reveal whose art has no
                       transferable source yet — a catalog rule, not a reveal
                       rule. The settled hover below re-lays-out at the grown
                       size, so the frame and text raster natively there and
                       the reserve keeps the art covered through the growth. */
                    { rasterReserveScale: 1.6 },
                );
            } catch (err) {
                console.error('SpellAnnounce: failed to render card face', err);
            }
        }
        if (!face) return null;
        // Enlargement and pointer rotation compose through the shared card
        // transform variables, so the wrapper remains the stable layout slot.
        face.classList.add('spell-announce-cardface');
        face.__moonfallPresentationCard = card;
        attachFloatHover(face);
        wrap.appendChild(face);
        if (Cards?.fitCardDescription) {
            Cards.fitCardDescription(face);
        }
        return wrap;
    }

    function display(announcement) {
        if (!isGameSurfaceActive()) {
            active = false;
            return;
        }
        if (!overlayElement) init();
        active = true;
        const generation = ++displayGeneration;
        /* The float owns the cast's cue as soon as it can paint a card face.
           The ONE remaining way it cannot is a card that fails to render at all,
           and that hands the cue straight back to the socket handler. */
        const releaseSoundOwnership = () => {
            if (typeof announcement.onPresentationAbandoned !== 'function') return;
            try {
                announcement.onPresentationAbandoned();
            } catch (error) {
                console.error('[SpellAnnounce] Failed to return sound ownership:', error);
            }
        };

        const stage = document.createElement('div');
        stage.className = 'spell-announce-stage';
        const compact = (announcement.cards || []).length > 2;
        if (compact) stage.classList.add('spell-announce-compact');

        const label = document.createElement('div');
        label.className = 'spell-announce-label';
        label.textContent = labelFor(announcement.kind, announcement.playerName);
        stage.appendChild(label);

        const cardsRow = document.createElement('div');
        cardsRow.className = 'spell-announce-cards';
        let rendered = 0;
        (announcement.cards || []).forEach((card, index) => {
            const wrap = buildCardFace(card, index, compact);
            if (wrap) {
                cardsRow.appendChild(wrap);
                rendered += 1;
            }
        });
        if (rendered === 0) {
            active = false;
            releaseSoundOwnership();
            showNext();
            return;
        }
        stage.appendChild(cardsRow);

        stage.addEventListener('mouseenter', pauseTimer);
        stage.addEventListener('mouseleave', resumeTimer);
        stage.addEventListener('click', hide);

        overlayElement.appendChild(stage);

        /* THE CAST IS SHOWN THE MOMENT IT STARTS. It is not held back, and a
           raster that never settles cannot delete it.

           This used to keep the whole stage at `visibility: hidden` until every
           card face reported a settled raster (or a 1.2s deadline expired), and
           to ABANDON the float outright when settlement failed. A cold enemy
           spell — the normal case, since you have never seen their deck — took
           most of that deadline, so the reveal landed near the end of the cast
           instead of at its start, and a failed raster meant the player never
           saw the card that hit them at all. ("show spells casted ... during
           casting not at the end of casting.")

           That is the readiness-gate trap AGENTS.md names outright: a warm-up
           probe may gate the ART SWAP, never the element. So the float paints
           now, text-first, and the card's own art lifecycle fills the well
           behind the shared placeholder — exactly the progressive rule in
           docs/asset-loading.md. Settlement below no longer decides WHETHER to
           show; it only decides how long a resolved cast is held open so its
           art is never cut off mid-load. */
        // Force a layout pass so the enter transition runs from a painted frame.
        void overlayElement.offsetWidth; // eslint-disable-line no-void
        overlayElement.classList.add('visible');
        window.dispatchEvent(new CustomEvent('moonfall:transient-change'));

        const stellarCastFace = announcement.playStellarCast === true
            ? cardsRow.querySelector('.card')
            : null;
        const customCastStarted = stellarCastFace
            ? Cards?.playStellarSpellCast?.(stellarCastFace, announcement.actionId) === true
            : false;
        if (!announcement.playStellarCast) {
            try { SoundEffects?.play?.('magic'); } catch (_) { }
        } else if (!customCastStarted) {
            console.warn('[SpellAnnounce] Stellar cast media was unavailable for an accepted cast.');
        }

        displayedAt = Date.now();
        sticky = announcement.sticky === true;
        stickyResolved = announcement.stickyResolved === true;
        activeMinStickyMs = announcement.playStellarCast === true
            ? Math.max(timings.minStickyMs, timings.stellarCastMinMs)
            : timings.minStickyMs;
        if (sticky) {
            // Held open until resolve() (the cast finished) — with a safety
            // net so a lost resolution signal can never freeze the overlay.
            scheduleHide(stickyResolved ? activeMinStickyMs : timings.stickySafetyMs);
        } else {
            scheduleHide(timings.holdMs + timings.extraPerCardMs * (rendered - 1));
        }

        const settlePresentation = Cards?.settleCardRasterForPresentation;
        const settlements = Array.from(cardsRow.querySelectorAll('.card')).map((face) => (
            typeof settlePresentation === 'function'
                ? settlePresentation(
                    face,
                    face.__moonfallPresentationCard,
                    { oversample: 1.6, stableGeometry: true },
                )
                : true
        ));
        if (!settlements.some((result) => result && typeof result.then === 'function')) return;
        if (generation !== displayGeneration) return;

        activeMediaSettled = false;
        const settledNow = () => {
            if (generation !== displayGeneration) return;
            if (mediaHoldTimer) {
                clearTimeout(mediaHoldTimer);
                mediaHoldTimer = null;
            }
            if (activeMediaSettled) return;
            activeMediaSettled = true;
            if (!hideOnMediaSettled) return;
            hideOnMediaSettled = false;
            // The art arrived after the cast resolved: grant the ordinary
            // minimum read time counted from the moment it became readable.
            scheduleHide(activeMinStickyMs);
        };
        mediaHoldTimer = setTimeout(settledNow, Math.max(0, Number(timings.mediaHoldMs) || 0));
        Promise.all(settlements.map((result) => Promise.resolve(result)))
            .then(settledNow, settledNow);
    }

    /**
     * The pending action resolved (story/state arrived): release a sticky
     * float, honoring the minimum display time so it never just flashes.
     */
    function resolve() {
        if (!active || !sticky || stickyResolved) {
            resolveQueuedSticky();
            return;
        }
        stickyResolved = true;
        if (!activeMediaSettled) {
            /* "never stop revealing the casted spell live as its loading unless
               player dissmissed it." The cast resolved before its card finished
               painting, so closing now would show the player a blank well and
               then take it away. Hold, and let the settlement handler start the
               minimum read time when the art is actually readable. A click still
               dismisses instantly, and the sticky safety net is still armed. */
            hideOnMediaSettled = true;
            return;
        }
        const shownFor = Date.now() - displayedAt;
        const waitMs = Math.max(0, activeMinStickyMs - shownFor);
        scheduleHide(waitMs); // keeps hover-pause bookkeeping consistent
    }

    function showNext() {
        if (active || queue.length === 0) return;
        if (!isGameSurfaceActive()) {
            queue = [];
            return;
        }
        display(queue.shift());
    }

    /**
     * Queue an announcement.
     * @param {Object} announcement {kind, cards: [cardData...], playerName?}
     */
    function show(announcement) {
        if (!announcement || !Array.isArray(announcement.cards) || announcement.cards.length === 0) return false;
        if (!isGameSurfaceActive()) {
            queue = [];
            return false;
        }
        queue.push(announcement);
        showNext();
        return true;
    }

    /** Immediately hide and drop anything queued (e.g. game ended). */
    function dismiss() {
        displayGeneration += 1;
        queue = [];
        clearHideTimer();
        // Without this the "art is still loading" flag survives the dismiss, and
        // the NEXT float — whose own card may settle synchronously, so nothing
        // ever clears it — treats its resolution as arriving mid-load and hangs
        // on the 90s safety net instead of taking its read time.
        clearMediaHold();
        Cards?.resetStellarSpellCastTracking?.();
        if (overlayElement) {
            overlayElement.classList.remove('visible', 'leaving');
            while (overlayElement.firstChild) overlayElement.removeChild(overlayElement.firstChild);
        }
        active = false;
        sticky = false;
        stickyResolved = false;
        activeMinStickyMs = timings.minStickyMs;
        window.dispatchEvent(new CustomEvent('moonfall:transient-change'));
    }

    /**
     * Socket `spellCastStarted` — float the full card when the caster is the
     * opponent. A local ordinary Spell needs no duplicate float, but a local
     * Stellar Spell uses this stable surface for its one-shot cast clip.
     */
    function handleSpellCastStarted(data, myPlayerId, {
        onPresentationAbandoned,
    } = {}) {
        if (!data || !data.card || !data.playerId) return false;
        if (!myPlayerId) return false;
        const isOwnCast = data.playerId === myPlayerId;
        const hasStellarCast = Cards?.shouldDeferSpellCastSound?.(data.card) === true;
        if (isOwnCast && !hasStellarCast) return false;
        return show({
            kind: isOwnCast ? 'yourStellarSpell' : 'enemySpell',
            cards: [data.card],
            playerName: data.playerName,
            sticky: true,
            playStellarCast: hasStellarCast,
            actionId: data.actionId,
            onPresentationAbandoned,
        });
    }


    /**
     * Socket `cardsRevealed` — the server already routed this event to the
     * right audience; classify only for the label.
     */
    function handleCardsRevealed(data, myPlayerId) {
        if (!data || !Array.isArray(data.cards) || data.cards.length === 0) return false;
        let kind = 'revealHand';
        if (data.zone === 'deck') {
            kind = data.ownerId === myPlayerId ? 'revealDeckSelf' : 'revealDeckEnemy';
        }
        return show({ kind, cards: data.cards });
    }

    function _setTimings(overrides) {
        Object.assign(timings, overrides || {});
    }

    return {
        init,
        show,
        dismiss,
        resolve,
        handleSpellCastStarted,
        handleCardsRevealed,
        _setTimings
    };
})();

export { SpellAnnounce };
